import { useGetReviewerPermissionByStep } from '@exam-center/admin-proctor-management';
import { FormLeavingListener } from '@exam-center/components';
import {
  type ExamCenterProctorRequestFragment,
  ExamCenterProctorRequestStep,
  AdminGetExamCenterProctorRequestByIdDocument,
  GetExamCenterProctorRequestByIdDocument,
  AdminSearchMyExamCenterProctorRequestsDocument,
  SearchExamCenterInReviewProctorRequestsDocument,
  SearchExamCenterRejectedProctorRequestsDocument,
  useCreateOrUpdateExamCenterProctorRequestVerdictMutation,
  useSubmitExamCenterProctorRequestVerdictMutation,
  Verdict,
} from '@exam-center/graphql';
import { useParams, useNavigate } from '@exam-center/router';
import { PaperSection, Box, Flex, Divider, Button, Form } from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { useSnackbar } from '@shared/snackbar';
import { useMemo } from 'react';

import {
  ExamCenterProctorRequestVerdictFormField,
  SubmitExamCenterProctorRequestVerdictConfirmModal,
} from '../../components';
import {
  ExamCenterProctorRequestVerdictFormProvider,
  type ExamCenterProctorRequestVerdictFormType,
  useAdminExamCenterProctorRequestDetails,
  useExamCenterProctorRequestDetailsFormContext,
} from '../../providers';
import { ExamCenterProctorRequestVerdictHistory } from '../exam-center-proctor-request-verdict-history';

interface ExamCenterProctorRequestVerdictProps {
  examCenterProctorRequest: ExamCenterProctorRequestFragment;
}

function ExamCenterProctorRequestVerdict(props: Readonly<ExamCenterProctorRequestVerdictProps>) {
  const { examCenterProctorRequest } = props;
  const { showSnackbar } = useSnackbar();
  const formContext = useExamCenterProctorRequestDetailsFormContext();
  const { requestId } = useParams('/admin/exam-proctors/requests/:requestId');
  const [isConfirmModalOpen, setConfirmModalOpen] = useDisclosure(false);
  const navigate = useNavigate();
  const [submitExamCenterProctorRequestVerdictMutation, { loading: isSubmitting }] =
    useSubmitExamCenterProctorRequestVerdictMutation({
      onCompleted: () => {
        showSnackbar({
          message: 'ยืนยันการอนุมัติคำขอเรียบร้อยแล้ว',
          variant: 'success',
        });
        navigate('/admin/exam-proctors/requests');
      },
      onError: () => {
        showSnackbar({
          message: 'เกิดข้อผิดพลาดในการยืนยันการอนุมัติคำขอ',
          variant: 'error',
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        AdminGetExamCenterProctorRequestByIdDocument,
        GetExamCenterProctorRequestByIdDocument,
        AdminSearchMyExamCenterProctorRequestsDocument,
        SearchExamCenterInReviewProctorRequestsDocument,
        SearchExamCenterRejectedProctorRequestsDocument,
      ],
    });

  const [createOrUpdateExamCenterProctorRequestVerdictMutation, { loading: isCreatingOrUpdating }] =
    useCreateOrUpdateExamCenterProctorRequestVerdictMutation({
      onCompleted: () => {
        showSnackbar({
          message: 'บันทึกผลการอนุมัติคำขอสำเร็จ',
          variant: 'success',
        });
      },
      onError: () => {
        showSnackbar({
          message: 'เกิดข้อผิดพลาดในการบันทึกผลการอนุมัติคำขอ',
          variant: 'error',
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        AdminGetExamCenterProctorRequestByIdDocument,
        GetExamCenterProctorRequestByIdDocument,
      ],
    });

  const handleSave = async (data: ExamCenterProctorRequestVerdictFormType) => {
    await createOrUpdateExamCenterProctorRequestVerdictMutation({
      variables: {
        createOrUpdateExamCenterProctorRequestVerdictInput: {
          proctorRequestId: requestId,
          verdict: data.verdict,
          comment: data.comment,
        },
      },
    });
  };

  const handleSubmit = async () => {
    formContext.setValue('action', 'submit');
    const isValid = await formContext.trigger();
    if (isValid) {
      setConfirmModalOpen.open();
    }
  };

  const handleConfirmSubmit = async () => {
    await formContext.handleSubmit(handleSave)();
    await submitExamCenterProctorRequestVerdictMutation({
      variables: {
        submitReviewExamCenterProctorRequestInput: {
          examCenterProctorRequestId: requestId,
        },
      },
    });
  };

  const isInReviewStep = [
    ExamCenterProctorRequestStep.InReview_1,
    ExamCenterProctorRequestStep.InReview_2,
  ].includes(examCenterProctorRequest.step);

  const { hasUpdateAccess } = useGetReviewerPermissionByStep(examCenterProctorRequest.step);

  const submittedVerdicts = useMemo(
    () => examCenterProctorRequest.verdicts.filter((verdict) => verdict.submittedAt !== null),
    [examCenterProctorRequest.verdicts],
  );

  return (
    <Form onSubmit={formContext.handleSubmit(handleSave)}>
      <Flex
        sx={(theme) => ({
          flex: 1,
          gap: theme.spacing(6),
          flexDirection: 'column',
        })}
      >
        {submittedVerdicts.length > 0 ? (
          <ExamCenterProctorRequestVerdictHistory verdicts={submittedVerdicts} />
        ) : null}
        {isInReviewStep && hasUpdateAccess ? (
          <>
            <PaperSection title='การอนุมัติคำขอ'>
              <Flex sx={{ height: '100%', flexDirection: 'column' }}>
                <Box sx={{ p: (theme) => theme.spacing(6), height: '100%' }}>
                  <ExamCenterProctorRequestVerdictFormField />
                </Box>
                <Divider />
                <Flex
                  sx={{
                    p: (theme) => theme.spacing(4),
                    height: '100%',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    type='submit'
                    variant='outlined'
                    color='secondary'
                    loading={isCreatingOrUpdating}
                  >
                    บันทึกผลการอนุมัติ
                  </Button>
                </Flex>
              </Flex>
            </PaperSection>
            <Button
              onClick={handleSubmit}
              size='large'
              sx={{ ml: 'auto', display: 'block' }}
              loading={isSubmitting}
            >
              ยืนยันการอนุมัติคำขอ
            </Button>
          </>
        ) : null}
      </Flex>
      <SubmitExamCenterProctorRequestVerdictConfirmModal
        open={isConfirmModalOpen}
        onClose={setConfirmModalOpen.close}
        onConfirm={handleConfirmSubmit}
        variant={formContext.getValues('verdict') === Verdict.Approved ? 'Approved' : 'Rejected'}
        isTerminal={examCenterProctorRequest.step !== 'IN_REVIEW_1'}
      />
      <FormLeavingListener onSubmit={handleSave} />
    </Form>
  );
}

export default function ExamCenterProctorRequestVerdictSelection() {
  const { examCenterProctorRequest } = useAdminExamCenterProctorRequestDetails();

  return (
    <ExamCenterProctorRequestVerdictFormProvider initialValues={examCenterProctorRequest}>
      <ExamCenterProctorRequestVerdict examCenterProctorRequest={examCenterProctorRequest} />
    </ExamCenterProctorRequestVerdictFormProvider>
  );
}
